import classNames from 'classnames';
import { identity } from 'lodash-es';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import Avatar from '../components/Avatar';
import Button from '../components/Button';
import DetailPreviewDescriptionContainer from '../components/DetailPreviewDescriptionContainer';
import ExternalBrandLogo from '../components/ExternalBrandLogo';
import SVGIcon from '../components/SVGIcon';
import QuillComponent from '../editor/QuillComponent';
import QuillContext from '../editor/QuillContext';
import { gql } from '../graphql/generated/gql';
import emailIcon from '../icons/non-prefilled/16x16/email.svg';
import postIcon from '../icons/non-prefilled/16x16/post.svg';
import subtopicsIcon from '../icons/non-prefilled/16x16/subtopics.svg';
import groupIcon from '../icons/prefilled/16x16/group.svg';
import NestableLink from '../utils/NestableLink';
import { deltaToText } from '../utils/text/deltaText';
import userPath from '../utils/url/userPath';
import useIsPublicView from '../utils/useIsPublicView';
import * as styles from './UserDetailPreview.module.scss';
import UserBadge from './profile/UserBadge';

const USER_DETAIL_PREVIEW_QUERY = gql(/** @complexity 10 */ `
  query UserDetailPreview($id: ID!) {
    user(id: $id) {
      id
      title
      name
      description
      membershipGroups {
        id
        name
      }
      membershipTopics(includeGroups: true) {
        id
      }
      ownedPostsCount: postsCount(filter: PUBLISHED)
      email
      importedFrom
      identities {
        id
        externalId
        platform
      }
      ...userPath_user
      ...UserBadge_user
      ...Avatar_user
    }
    microsoftIntegration: integration(platform: "microsoft") {
      id
    }
    # eslint-disable-next-line @graphql-eslint/require-selections
    slackIntegration: integration(platform: "slack") {
      externalId
    }
    slackUser(slabId: $id) {
      id
    }
  }
`);

const PUBLIC_USER_DETAIL_PREVIEW_QUERY = gql(/** @complexity 1 */ `
  query PublicUserDetailPreview($id: ID!) {
    publicUser(id: $id) {
      id
      title
      name
      ...Avatar_user
    }
  }
`);

const TITLE_HEIGHT = 80;
const MIN_CONTENT_HEIGHT = 30;
const FOOTER_HEIGHT = 69;

interface UseDetailPreviewProps {
  userId: string;
  setMinHeight: (height: number | null) => void;
}

const UserDetailPreview = ({ userId, setMinHeight }: UseDetailPreviewProps) => {
  const isPublicView = useIsPublicView();
  const contentRef = useRef<HTMLDivElement>(null);

  const [isContentReady, setIsContentReady] = useState(false);

  const { data } = useQuery(USER_DETAIL_PREVIEW_QUERY, {
    variables: { id: userId },
    skip: isPublicView,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all', // ignore failing integration fields
  });

  const { data: publicData } = useQuery(PUBLIC_USER_DETAIL_PREVIEW_QUERY, {
    variables: { id: userId },
    skip: !isPublicView,
    fetchPolicy: 'cache-first',
  });

  const user = isPublicView ? publicData?.publicUser : data?.user;

  const internalUser = data?.user;
  const content = internalUser?.description;

  const hasContent = internalUser && deltaToText(content);

  const shouldShowStats =
    internalUser &&
    (internalUser?.membershipGroups.length > 0 ||
      internalUser.membershipTopics.length > 0 ||
      internalUser.ownedPostsCount > 0);

  const slackIdentity = internalUser?.identities?.find(
    (i) => i?.platform === 'slack',
  );
  const slackUserId = slackIdentity?.externalId ?? data?.slackUser?.id;
  const slackTeamId = data?.slackIntegration?.externalId;

  const shouldShowSlack = !!(slackTeamId && slackUserId);
  const shouldShowTeams = !!data?.microsoftIntegration;

  const showProfiles =
    internalUser &&
    !internalUser?.importedFrom &&
    (internalUser.email || shouldShowSlack || shouldShowTeams);

  const showFooter = shouldShowStats || showProfiles;

  useEffect(() => {
    if (!user) return undefined;

    const minHeight =
      TITLE_HEIGHT +
      (hasContent ? MIN_CONTENT_HEIGHT : 0) +
      (showFooter ? FOOTER_HEIGHT : 0);

    setMinHeight(minHeight);

    return () => {
      setMinHeight(null);
    };
  }, [hasContent, setMinHeight, user, showFooter]);

  useLayoutEffect(() => {
    if (contentRef.current) {
      const verifyContentReady = () => {
        if (contentRef?.current?.getBoundingClientRect().height) {
          setIsContentReady(true);
        }
      };
      const resizeObserver = new ResizeObserver(verifyContentReady);
      resizeObserver.observe(contentRef.current);
      verifyContentReady();

      return () => resizeObserver.disconnect();
    }
    return undefined;
  }, [user]);

  if (!user) {
    return null;
  }

  return (
    <NestableLink className={styles.overlayLink} newTab to={userPath(user)}>
      <div ref={contentRef} className={styles.content}>
        <div className={styles.headerSection}>
          <div className={styles.header}>
            <h3>
              {user.name}
              {internalUser && (
                <span className={styles.userBadge}>
                  <UserBadge user={internalUser} />
                </span>
              )}
            </h3>
            {user.title && <div className={styles.title}>{user.title}</div>}
          </div>
          <div className={styles.avatar}>
            <Avatar size={56} user={user} />
          </div>
        </div>
        <DetailPreviewDescriptionContainer
          content={content}
          contentReady={isContentReady}
        >
          {hasContent && (
            <QuillContext>
              <QuillComponent
                content={content}
                rootClassName={styles.editorRoot}
                contentPlaceholder=""
                namespace="postPreview"
                readOnly
                smallTitleSize
                noTitleLine
                titlePlaceholder="Untitled"
              />
            </QuillContext>
          )}
        </DetailPreviewDescriptionContainer>
        {showFooter && (
          <div className={styles.footer}>
            {shouldShowStats && (
              <>
                {internalUser.membershipGroups.length > 0 && (
                  <div
                    className={classNames(styles.actionButton, styles.groups)}
                  >
                    <SVGIcon className={styles.groupIcon}>{groupIcon}</SVGIcon>
                    {internalUser.membershipGroups[0]?.name}
                    {internalUser.membershipGroups.length > 1 &&
                      ` + ${internalUser.membershipGroups.length - 1}`}
                  </div>
                )}
                {internalUser.membershipTopics.length > 0 && (
                  <div
                    className={classNames(styles.actionButton, styles.topics)}
                  >
                    <SVGIcon className={styles.topicsIcon}>
                      {subtopicsIcon}
                    </SVGIcon>
                    <div className={styles.amount}>
                      {internalUser.membershipTopics.length}
                    </div>
                  </div>
                )}
                {internalUser.ownedPostsCount > 0 && (
                  <div
                    className={classNames(styles.actionButton, styles.posts)}
                  >
                    <SVGIcon className={styles.postsIcon}>{postIcon}</SVGIcon>
                    <div className={styles.amount}>
                      {internalUser.ownedPostsCount}
                    </div>
                  </div>
                )}
              </>
            )}
            {showProfiles && (
              <div className={styles.profiles}>
                {internalUser.email && (
                  <Button
                    className={styles.identity}
                    to={`mailto:${internalUser.email}`}
                    newTab
                  >
                    <SVGIcon>{emailIcon}</SVGIcon>
                  </Button>
                )}
                {shouldShowSlack && (
                  <Button
                    className={styles.identity}
                    to={`slack://user?team=${slackTeamId}&id=${slackUserId}`}
                  >
                    <ExternalBrandLogo brand="slack" height={16} />
                  </Button>
                )}
                {shouldShowTeams && (
                  <Button
                    className={styles.identity}
                    {...identity(
                      internalUser.email
                        ? {
                            to: `https://teams.microsoft.com/l/chat/0/0?users=${internalUser.email}`,
                            newTab: true,
                          }
                        : {},
                    )}
                  >
                    <ExternalBrandLogo
                      brand="microsoft"
                      product="teams"
                      height={16}
                    />
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </NestableLink>
  );
};

export default UserDetailPreview;
